<template>
  <div>
    <div class="lib__form__main" data-cy="resume-component">
      <ValidationObserver class="lib__form__main__content" ref="validator" tag="div">
        <header class="lib__form__main__header">
          <h2 class="lib__h2">{{ $t('complaint.resume.title') }}</h2>
        </header>
        <resume-section-title minimumBadgeWidth :read-only="readOnly" :step="1" @goToStep="goToStep($event)">
          <template v-slot:icon>1</template>
          <template>{{ $t('complaint.resume.category') }}</template>
        </resume-section-title>
        <resume-container commonMobileAndDesktopLayout>
          <template v-slot:common>
            <div class="lib__panel lib__no-margin panel__main--reduced-margin">
              <div class="lib__panel__main">
                <ul style="padding-left: 70px">
                  <li>{{ $t('complaint.category.' + categorySelectionState.parentCategory.key) }}</li>
                </ul>
              </div>
              <div class="lib__panel__main">
                <ul style="padding-left: 100px" v-if="categorySelectionState.category">
                  <li>{{ $t('complaint.category.' + categorySelectionState.category.key) }}</li>
                </ul>
              </div>
            </div>
          </template>
        </resume-container>

        <resume-section-title
          minimumBadgeWidth
          noMarginTop
          :read-only="readOnly"
          :step="2"
          @goToStep="goToStep($event)"
        >
          <template v-slot:icon>2</template>
          <template>{{ $t('complaint.resume.identification') }}</template>
        </resume-section-title>
        <resume-container commonMobileAndDesktopLayout>
          <template v-slot:common>
            <InfoTable>
              <template>
                <InfoTableItem v-if="identificationState.identificationType === 'ean'">
                  <template v-slot:icon><i class="lib__infotable__icon fas fa-cog"></i></template>
                  <template v-slot:label>{{ $t('complaint.resume.eanCode') }}</template>
                  <template v-slot:value>{{ identificationState.eanInfo.eanCode }}</template>
                </InfoTableItem>
                <InfoTableItem>
                  <template v-slot:icon><i class="lib__infotable__icon fas fa-map-marker-alt"></i></template>
                  <template v-slot:label>{{ $t('complaint.resume.address') }} :</template>
                  <template v-slot:value>{{ address }}</template>
                </InfoTableItem>
              </template>
            </InfoTable>
          </template>
        </resume-container>

        <resume-section-title
          minimumBadgeWidth
          noMarginTop
          :read-only="readOnly"
          :step="3"
          @goToStep="goToStep($event)"
        >
          <template v-slot:icon>3</template>
          <template>{{ $t('complaint.resume.complaintInfo') }}</template>
        </resume-section-title>
        <resume-container commonMobileAndDesktopLayout>
          <template v-slot:common>
            <div class="lib__panel" style="margin-bottom: 10px">
              <InfoTable>
                <InfoTableItem v-if="energySelectionInfoState.energySelection && energyRequired">
                  <template v-if="energySelectionInfoState.energySelection.electricity" v-slot:icon>
                    <i class="lib__infotable__icon fas">
                      <img
                        class="lib__infotable__icon"
                        style="width: 14px; height: 16px"
                        alt="Electicity"
                        :src="require('@ores/css-library/assets/img/icons/electricity.svg')"
                      />
                    </i>
                  </template>
                  <template v-else-if="energySelectionInfoState.energySelection.gas" v-slot:icon>
                    <i class="lib__infotable__icon fas">
                      <img
                        class="lib__infotable__icon"
                        style="width: 14px; height: 16px"
                        alt="Gas"
                        :src="require('@ores/css-library/assets/img/icons/gas.svg')"
                      />
                    </i>
                  </template>
                  <template v-slot:label>{{ $t('complaint.resume.energy') }} : </template>
                  <template v-slot:value>{{
                    $t(`energy.${energySelectionInfoState.energySelection.electricity ? 'electricity' : 'gas'}`)
                  }}</template>
                </InfoTableItem>
                <InfoTableItem v-if="energySelectionInfoState.eanCode">
                  <template v-slot:icon><i class="lib__infotable__icon fas fa-cog"></i></template>
                  <template v-slot:label>{{ $t('common.identification.eanCode') }} :</template>
                  <template v-slot:value>{{ energySelectionInfoState.eanCode }}</template>
                </InfoTableItem>
                <InfoTableItem v-if="energySelectionInfoState.cabinNumber">
                  <template v-slot:icon><i class="lib__infotable__icon fas fa-house-damage"></i></template>
                  <template v-slot:label>{{ $t('complaint.resume.cabinNumber') }} :</template>
                  <template v-slot:value>{{ energySelectionInfoState.cabinNumber }}</template>
                </InfoTableItem>
                <InfoTableItem v-if="energySelectionInfoState.streetlightNumber">
                  <template v-slot:icon><i class="lib__infotable__icon fas fa-lightbulb"></i></template>
                  <template v-slot:label>{{ $t('complaint.resume.streetlightNumber') }}</template>
                  <template v-slot:value>{{ energySelectionInfoState.streetlightNumber }}</template>
                </InfoTableItem>
                <InfoTableItem v-if="energySelectionInfoState.rechargingTerminal">
                  <template v-slot:icon><i class="lib__infotable__icon fas fa-house-damage"></i></template>
                  <template v-slot:label>{{ $t('complaint.resume.rechargingTerminal') }}</template>
                  <template v-slot:value>{{ energySelectionInfoState.rechargingTerminal }}</template>
                </InfoTableItem>
                <InfoTableItem v-if="complaintInfoState.fileNumber">
                  <template v-slot:icon><i class="lib__infotable__icon fas fa-folder"></i></template>

                  <template v-slot:label>{{ $t('complaint.resume.fileNumber') }} :</template>
                  <template v-slot:value>{{ complaintInfoState.fileNumber }}</template>
                </InfoTableItem>
                <InfoTableItem v-if="complaintInfoState.email">
                  <template v-slot:icon><i class="lib__infotable__icon fas fa-envelope"></i></template>
                  <template v-slot:label>{{ $t('common.contact.form.email') }} :</template>
                  <template v-slot:value>{{ complaintInfoState.email }}</template>
                </InfoTableItem>
                <InfoTableItem v-if="complaintInfoState.damageType">
                  <template v-slot:icon><i class="lib__infotable__icon fas fa-tools"></i></template>
                  <template v-slot:label>{{ $t('complaint.complaintInfo.damageType') }} :</template>
                  <template v-slot:value>{{ $t('common.damageType.' + complaintInfoState.damageType) }}</template>
                </InfoTableItem>
                <InfoTableItem v-if="complaintInfoState.sinisterDate">
                  <template v-slot:icon><i class="lib__infotable__icon fas fa-calendar"></i></template>
                  <template v-slot:label>{{ $t('complaint.complaintInfo.sinisterDate') }} :</template>
                  <template v-slot:value>{{ sinisterDate }}</template>
                </InfoTableItem>
                <InfoTableItem v-if="complaintInfoState.bankAccountIban">
                  <template v-slot:icon><i class="lib__infotable__icon fas fa-euro-sign"></i></template>
                  <template v-slot:label>{{ $t('complaint.resume.bankAccountInformation') }} :</template>
                  <template v-slot:value
                    >{{ complaintInfoState.bankAccountIban }}
                    <span v-if="complaintInfoState.bankAccountBic">
                      - {{ complaintInfoState.bankAccountBic }}</span
                    ></template
                  >
                </InfoTableItem>
                <InfoTableItem v-if="complaintInfoState && complaintInfoState.isIndemnitySendToLegal">
                  <template v-slot:icon><i class="lib__infotable__icon fas fa-check-square"></i></template>
                  <template v-slot:label>{{ $t('complaint.resume.additionalInfo') }} :</template>
                  <template v-slot:value>
                    <p style="max-width:90%;">{{ $t('complaint.resume.gotDamageAndWantCashback') }}</p>
                  </template>
                </InfoTableItem>
                <InfoTableItem>
                  <template v-slot:icon><i class="lib__infotable__icon fas fa-align-justify"></i></template>
                  <template v-slot:label>{{ $t('complaint.resume.description') }} :</template>
                  <template v-slot:value>
                    <p style="max-width:90%;">{{ description }}</p>
                  </template>
                </InfoTableItem>
                <InfoTableItem>
                  <template v-slot:icon><i class="lib__infotable__icon fas fa-paperclip"></i></template>
                  <template v-slot:label>{{ $t('complaint.resume.attachedDocuments') }} :</template>
                  <template v-slot:value>
                    <template v-if="attachmentsState.length > 0">
                      <div v-for="file in attachmentsState" :key="file.key">
                        <span style="max-width:90%;">{{ file.name }}</span>
                      </div>
                    </template>
                    <template v-else>
                      <span>-</span>
                    </template>
                  </template>
                </InfoTableItem>
              </InfoTable>
            </div>
          </template>
        </resume-container>

        <resume-section-title
          minimumBadgeWidth
          noMarginTop
          :read-only="readOnly"
          :step="4"
          @goToStep="goToStep($event)"
        >
          <template v-slot:icon>4</template>
          <template>{{ $t('complaint.resume.contact') }}</template>
        </resume-section-title>
        <resume-container borderless commonMobileAndDesktopLayout>
          <template v-slot:common>
            <div class="lib__panel" style="margin-bottom: 10px">
              <div class="lib__panel__main">
                <div class="lib__text-small lib__text-gray-500">{{ $t('complaint.resume.requestor') }}</div>
                <ul class="lib__list lib__list--icon">
                  <li>
                    <i class="fas fa-id-card"></i>
                    {{ $t('common.contact.type.' + contactInfoState.contactBase.contactType) }}
                  </li>
                  <li v-if="companyInfo">
                    <i class="fas fa-building"></i>
                    {{ companyInfo }}
                  </li>
                  <li v-if="!!contactVat">
                    <i class="fas fa-file-invoice"></i>
                    {{ $t('complaint.resume.vatNumber') }} {{ contactVat }}
                  </li>
                  <li>
                    <i class="fas fa-user"></i>
                    {{ contactName }}
                  </li>
                  <li v-if="!!contactInfoState.contactBase.job">
                    <i class="fas fa-briefcase"></i>
                    {{ contactInfoState.contactBase.job }}
                  </li>
                  <li>
                    <i class="fas fa-envelope"></i>
                    {{ contactInfoState.contactBase.email }}
                  </li>
                  <li v-if="!!contactInfoState.contactBase.phone">
                    <i class="fas fa-phone"></i>
                    {{ contactInfoState.contactBase.phone }}
                  </li>
                  <li v-if="!!contactInfoState.contactBase.mobile">
                    <i class="fas fa-mobile-alt"></i>
                    {{ contactInfoState.contactBase.mobile }}
                  </li>
                  <li>
                    <i class="fas fa-map-marker-alt"></i>
                    <template v-if="contactInfoState.otherAddress">
                      {{ contactInfoState.otherAddress.streetSubCity.name }}
                      {{ contactInfoState.otherAddress.houseNumber }} {{ contactInfoState.otherAddress.houseBox }} -
                      {{ contactInfoState.otherAddress.subCity.zipCode }}
                      {{ contactInfoState.otherAddress.subCity.cityName }} {{ contactInfoState.otherAddress.country }}
                    </template>
                    <template v-else>{{ address }}</template>
                  </li>
                </ul>
              </div>
            </div>

            <div class="lib__panel" style="margin-top: 10px" v-if="contactInfoState.addOtherContact">
              <div class="lib__panel__main">
                <div class="lib__text-small lib__text-gray-500">{{ $t('complaint.resume.otherContact') }}</div>
                <ul class="lib__list lib__list--icon">
                  <li>
                    <i class="fas fa-user"></i>
                    {{ otherContactName }}
                  </li>
                  <li>
                    <i class="fas fa-envelope"></i>
                    {{ contactInfoState.otherContact.email }}
                  </li>
                  <li v-if="!!contactInfoState.otherContact.phone">
                    <i class="fas fa-phone"></i>
                    {{ contactInfoState.otherContact.phone }}
                  </li>
                  <li v-if="!!contactInfoState.otherContact.mobile">
                    <i class="fas fa-mobile-alt"></i>
                    {{ contactInfoState.otherContact.mobile }}
                  </li>
                  <li>
                    <i class="fas fa-map-marker-alt"></i>
                    {{ contactInfoState.otherContact.address.streetSubCity.name }}
                    {{ contactInfoState.otherContact.address.houseNumber }}
                    {{ contactInfoState.otherContact.address.houseBox }} -
                    {{ contactInfoState.otherContact.address.subCity.zipCode }}
                    {{ contactInfoState.otherContact.address.subCity.cityName }}
                    {{ contactInfoState.otherContact.address.country }}
                  </li>
                  <li>
                    <i class="fas fa-align-justify"></i>
                    {{ contactInfoState.otherContact.description }}
                  </li>
                </ul>
              </div>
            </div>
          </template>
        </resume-container>

        <Recaptcha siteKey="MTPublic-xmXbmt5Mn" v-if="!readOnly && customerViewToken === null && jwtToken === null" v-model="recaptcha" />
      </ValidationObserver>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Prop } from 'vue-property-decorator'
import Step from '@/components/steps/Step'
import ResumeAddress from '@/components/display/ResumeAddress.vue'
import {
  ApiErrorNotification,
  CategorySelectionStepModel,
  ComplaintInfo,
  ContactInfo,
  EnergySelectionStepModel,
  FileInfo,
  IdentificationModel,
} from '@/models'
import { State } from 'vuex-class'
import { complaintService, dateService, helper } from '@/services'

@Component({
  name: 'summary-step',
  components: {
    ResumeAddress,
  },
})
export default class SummaryStep extends Step {
  //#region [Property]
  @Prop({ type: Boolean, default: false }) public readonly readOnly!: boolean
  //#endregion

  //#region [Data]
  @State('apiErrors', { namespace: 'apiErrors' })
  public readonly apiErrors!: ApiErrorNotification | null

  @State('requestId', { namespace: 'complaint' })
  public requestId!: string

  @State('lang')
  public lang!: string | null

  @State('category', { namespace: 'complaint/categorySelection' })
  public categorySelectionState!: CategorySelectionStepModel | null

  @State('identification', { namespace: 'complaint/identification' })
  public identificationState!: IdentificationModel | null

  @State('energySelectionStepInfo', { namespace: 'complaint/energy' })
  public energySelectionInfoState!: EnergySelectionStepModel | null

  @State('complaintInfo', { namespace: 'complaint/complaintInfo' })
  public complaintInfoState!: ComplaintInfo | null

  @State('contactInfo', { namespace: 'complaint/contactInfo' })
  public contactInfoState!: ContactInfo | null

  @State('attachments', { namespace: 'complaint/attachments' })
  public attachmentsState!: FileInfo[]

  public recaptcha: string | null = null
  //#endregion

  //#region [Computed]
  public get energyRequired(): boolean {
    return complaintService.isInList(this.categorySelectionState, complaintService.energyRequiredList)
  }

  public get jwtToken(): string | null {
    return sessionStorage.getItem('token')
  }

  public get customerViewToken(): string | null {
    return sessionStorage.getItem('customerViewToken')
  }

  public get hasAttachedDocuments(): boolean {
    return this.attachmentsState !== null && this.attachmentsState.length > 0
  }

  private get address() {
    let str = ''
    if (this.identificationState && this.identificationState.identificationType === 'address') {
      const addressPoint = this.identificationState.address!
      if (addressPoint.streetSubCity) {
        if (addressPoint.houseNumber) {
          str += addressPoint.houseNumber
        }
        if (addressPoint.houseBox) {
          str += ' / ' + addressPoint.houseBox
        }
        str += ' ' + addressPoint.streetSubCity.name + ', '
      }
      str += addressPoint.subCity.zipCode
      str += ' ' + addressPoint.subCity.cityName
      return str
    }
    if (this.identificationState && this.identificationState.identificationType === 'ean') {
      const consumptionAddress = this.identificationState.eanInfo?.consumptionAddress!
      str += consumptionAddress.houseNumber
      str += ' ' + consumptionAddress.streetName
      if (consumptionAddress.complement) {
        str += ' ' + consumptionAddress.complement
      }
      if (consumptionAddress.complementBox) {
        str += ' ' + consumptionAddress.complementBox
      }
      if (consumptionAddress.floorNumber) {
        str += ` (${this.$t(`common.identification.floor`).toString()}: ${consumptionAddress.floorNumber})`
      }
      str += ', ' + consumptionAddress.zipCode
      str += ' ' + consumptionAddress.cityName
      return str
    }
    return ''
  }

  public get contactName(): string {
    if (this.contactInfoState && this.contactInfoState.contactBase) {
      let civility = null
      if (this.contactInfoState.contactBase.civility === 1) {
        civility = this.$t('contact.civility.mr')
      } else if (this.contactInfoState.contactBase.civility === 2) {
        civility = this.$t('contact.civility.mrs')
      }
      return `${civility} ${this.contactInfoState.contactBase.firstName} ${this.contactInfoState.contactBase.lastName}`
    }
    return ''
  }

  public get otherContactName(): string {
    if (this.contactInfoState && this.contactInfoState.otherContact) {
      let civility = null
      if (this.contactInfoState.otherContact.civility === 1) {
        civility = this.$t('contact.civility.mr')
      } else if (this.contactInfoState.otherContact.civility === 2) {
        civility = this.$t('contact.civility.mrs')
      }
      return `${civility} ${this.contactInfoState.otherContact.firstName} ${this.contactInfoState.otherContact.lastName}`
    }
    return ''
  }

  public get contactMail(): string {
    if (this.contactInfoState && this.contactInfoState.contactBase) {
      return this.contactInfoState.contactBase.email!
    }
    return ''
  }

  get contactVat(): string | undefined | null {
    return this.contactInfoState?.contactBase?.vatNumber?.trim()
  }

  get companyInfo(): string | undefined | null {
    const contact = this.contactInfoState?.contactBase
    if (contact?.contactType === 'publicAuthority') {
      return `${this.$t('common.contact.publicAuthorityType.' + contact?.companyType)} ${contact?.companyName ?? ''}`
    } else if (contact?.contactType === 'freelanceOrCompany') {
      return `${this.$t('common.contact.companyType.' + contact?.companyType)} ${contact?.companyName ?? ''}`
    }
  }

  public get sinisterDate(): string {
    return this.complaintInfoState?.sinisterDate
      ? dateService.displayDateText(this.complaintInfoState!.sinisterDate)
      : ''
  }

  public get description(): string | null {
    return !this.complaintInfoState ? null : this.complaintInfoState.description
  }

  public sinisterDateValue(): Date | null {
    return complaintService.isInList(this.categorySelectionState, complaintService.indemnityRequestList) ||
      complaintService.isInList(this.categorySelectionState, complaintService.indemnitySendToLegal)
      ? this.complaintInfoState!.sinisterDate
      : null
  }
  //#endregion

  //#region [Method]
  public goToStep(step: number) {
    this.$emit('goToStep', step)
  }

  public async sendNewComplaint() {
    const files = []
    if (this.attachmentsState && this.attachmentsState.length > 0) {
      for (const file of this.attachmentsState) {
        files.push(file.name)
      }
    }

    let energy = null
    if (this.energyRequired) {
      if (this.energySelectionInfoState!.energySelection?.electricity) {
        energy = 1
      } else if (this.energySelectionInfoState!.energySelection?.gas) {
        energy = 2
      }
    }

    let otherAddress = null
    if (this.contactInfoState!.useOtherAddress) {
      otherAddress = {
        street: this.contactInfoState!.otherAddress!.streetSubCity?.name,
        houseNumber: this.contactInfoState!.otherAddress!.houseNumber,
        houseBox: this.contactInfoState!.otherAddress!.houseBox,
        zipCode: this.contactInfoState!.otherAddress!.subCity?.zipCode,
        subCity: this.contactInfoState!.otherAddress!.subCity?.cityName,
        country: this.contactInfoState!.otherAddress!.country,
      }
    }

    let otherContactPerson: any = null
    if (this.contactInfoState!.addOtherContact && this.contactInfoState!.otherContact) {
      otherContactPerson = helper.clone(this.contactInfoState!.otherContact)
      otherContactPerson.address = {
        street: this.contactInfoState!.otherContact.address.streetSubCity?.name,
        houseNumber: this.contactInfoState!.otherContact.address.houseNumber,
        houseBox: this.contactInfoState!.otherContact.address.houseBox,
        zipCode: this.contactInfoState!.otherContact.address.subCity?.zipCode,
        subCity: this.contactInfoState!.otherContact.address.subCity?.cityName,
        country: this.contactInfoState!.otherContact.address.country,
      }
    }

    const data = {
      language: this.lang,
      webReference: this.requestId,
      complaintParentCategory: this.categorySelectionState!.parentCategory!.key,
      complaintParentCategoryLabel: this.$t('complaint.category.' + this.categorySelectionState!.parentCategory!.key),
      complaintCategory: this.categorySelectionState!.category ? this.categorySelectionState!.category.key : null,
      complaintCategoryLabel: this.categorySelectionState!.category
        ? this.$t('complaint.category.' + this.categorySelectionState!.category!.key)
        : null,
      complaintAddress:
        this.identificationState?.identificationType === 'address' && this.identificationState.address
          ? {
              zipCode: this.identificationState.address.subCity.zipCode,
              subCity: this.identificationState.address.subCity.cityName,
              streetSubCity: this.identificationState.address.streetSubCity?.name,
              houseNumber: this.identificationState.address.houseNumber,
              houseBox: this.identificationState.address.houseBox,
              isMapLocation: this.identificationState.address.isMapLocation,
              mapLocation: this.identificationState.address.mapLocation,
            }
          : null,
      consumptionAddress:
        this.identificationState?.identificationType === 'ean' && this.identificationState.eanInfo
          ? this.identificationState.eanInfo.consumptionAddress
          : null,
      eanNumber:
        this.identificationState?.identificationType === 'ean'
          ? this.identificationState.eanCode?.trim()
          : this.energySelectionInfoState?.eanCode,
      energy,
      streetLightRef: complaintService.isInList(
        this.categorySelectionState,
        complaintService.streetLightRefOptionalList,
      )
        ? this.energySelectionInfoState!.streetlightNumber
        : null,
      cabinNumber: complaintService.isInList(this.categorySelectionState, complaintService.cabinNumberOptionalList)
        ? this.energySelectionInfoState!.cabinNumber
        : null,
      rechargingTerminal: complaintService.isInList(
        this.categorySelectionState,
        complaintService.rechargingTerminalOptionalList,
      )
        ? this.energySelectionInfoState!.rechargingTerminal
        : null,
      bankAccountBic: complaintService.isInList(this.categorySelectionState, complaintService.indemnityRequestList)
        ? this.complaintInfoState!.bankAccountBic
        : null,
      bankAccountIban: complaintService.isInList(this.categorySelectionState, complaintService.indemnityRequestList)
        ? this.complaintInfoState!.bankAccountIban
        : null,
      damageType:
      complaintService.isInList(this.categorySelectionState, complaintService.damageTypeRequiredList)
        ? this.complaintInfoState!.damageType
        : null,
      sinisterDate: this.sinisterDateValue(),
      complaintDescription: this.description,
      isIndemnitySendToLegal:
      complaintService.isInList(this.categorySelectionState, complaintService.indemnitySendToLegal)
        ? this.complaintInfoState?.isIndemnitySendToLegal
        : false,
      attachments: files,
      requestorInfos: this.contactInfoState!.contactBase,
      addOtherAddress: this.contactInfoState!.useOtherAddress,
      otherAddress,
      addOtherContactPerson: this.contactInfoState!.addOtherContact,
      otherContactPerson,
    }

    if (this.customerViewToken !== null) {
      const loginResponse = await this.$api.get('api/login/token/customer-view', {
        headers: { Authorization: this.customerViewToken },
      })
      sessionStorage.setItem('token', loginResponse.data)
    } else if (this.jwtToken === null) {
      const loginResponse = await this.$api.get('api/login/token', {
        headers: { 'g-recaptcha-response': this.recaptcha! },
      })
      sessionStorage.setItem('token', loginResponse.data)
    }

    const response = await this.$api.post('api/complaintform', data, {
      headers: { Authorization: sessionStorage.getItem('token') },
    })
    if (response.status >= 200 && response.status < 300) {
      this.$appInsights.trackEvent('Complaint_StepSummary_Validated', {
        Segment: 'Complaint',
        WebRef: this.requestId,
        NewOrTracking: 'new',
        ParentCategoryCategory: this.categorySelectionState!.parentCategory!.key,
        Category: this.categorySelectionState!.category
          ? this.categorySelectionState!.category!.key
          : this.categorySelectionState!.parentCategory!.key,
      })

      return true
    }
  }

  public async save(next: true, valid: boolean) {
    if (!next || !valid) {
      return true
    }

    return await this.sendNewComplaint()
  }
  //#endregion
}
</script>

<style scoped>
.lib__infotable {
  border-left: 0px solid white;
}

.panel__main--reduced-margin .lib__panel__main {
  padding: 10px 0px;
}

.panel__main--reduced-margin .lib__panel__main:first-of-type {
  padding: 15px 0px 0px 0px;
}

.panel__main--reduced-margin .lib__panel__main:last-of-type {
  padding: 0px 0px 15px 0px;
}
</style>